import { Mail, MapPin } from "lucide-react";
import { Eyebrow, Reveal, SectionShell } from "./Shared";
import jacobPhoto from "@/assets/jacob-lee.webp";
import brianPhoto from "@/assets/brian-stretch.jpg";

const FOCUS_TAGS = ["Strategy", "AI · SI", "Co-Brand", "Direction", "Growth"];

export default function Team() {
  return (
    <SectionShell id="team" className="bg-surface/40">
      <Reveal>
        <Eyebrow className="mb-5">— 05 / The Team</Eyebrow>
        <h2 className="font-display text-4xl uppercase leading-[1.05] sm:text-5xl lg:text-6xl">
          Twenty years at the helm.{" "}
          <span className="text-volt">A new generation on the court.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
          Two decades of institutional experience, handed to a new generation of
          athlete representation.
        </p>
      </Reveal>

      <div className="mt-14 grid items-stretch gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-5 h-full" delay={0.1}>
          <div className="relative h-full min-h-[340px] sm:min-h-[420px]" data-testid="team-brian-photo-frame">
            <div
              className="absolute -inset-4 -z-10 rounded-[2rem] bg-volt/10 blur-3xl"
              aria-hidden="true"
            />
            <div className="img-blend absolute inset-0">
              <img
                src={brianPhoto}
                alt="Brian Stretch — Founder, SMG Sports Management Group International"
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                data-testid="team-brian-photo"
              />
            </div>
            <div className="absolute bottom-2 left-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-volt">
                The Pilot of the Ship
              </p>
              <p className="mt-1 font-display text-lg uppercase text-white">Brian Stretch</p>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.2}>
          <div className="rounded-3xl border border-white/10 bg-ink p-8 sm:p-10" data-testid="team-brian-card">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
              Founder · Creative Director — MBA, ESQ.
            </p>
            <h3 className="mt-2 font-display text-4xl uppercase text-white sm:text-5xl">
              Brian Stretch
            </h3>

            <blockquote className="mt-7 border-l-2 border-volt pl-5 text-xl font-medium leading-relaxed text-white sm:text-2xl">
              "Twenty years in the rooms where careers are decided. Now that
              room is behind every athlete we sign."
            </blockquote>

            <div className="mt-7 grid grid-cols-3 gap-3" data-testid="team-brian-stats">
              {[
                { value: "20+", label: "Years representing athletes" },
                { value: "$340M+", label: "In partnerships structured" },
                { value: "Pro Rooms", label: "NFL · MLB · Combat · Action" },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl border border-white/10 bg-elev p-3.5 sm:p-4">
                  <p className="font-display text-lg uppercase text-volt sm:text-2xl">{s.value}</p>
                  <p className="mt-1 text-[10px] uppercase leading-snug tracking-wider text-slate-400 sm:text-[11px]">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-7 space-y-5 text-base leading-relaxed text-slate-300">
              <p>
                Twenty years. NFL locker rooms, October dugouts, championship
                corners, start-gate corrals — Brian Stretch has spent two decades
                representing and working alongside some of the most recognizable
                names in professional sports, and curating some of the largest
                partnership opportunities in the industry.
              </p>
              <p>
                In 2023, he sold the firm's traditional representation division —
                not to walk away from sports, but to build the agency he always
                wished existed: contracts, brand-building, media and partnerships
                under one roof. That house became SMG Media Group, with studios
                in Carlsbad and Nashville.
              </p>
              <p className="font-semibold text-white">
                Now every relationship, every system and every lesson from two
                decades points at the fastest-growing court in the world. Sign
                with SMG and you don't get a contract manager — you get the pilot
                of the ship in your corner, for every swing on and off the court.
              </p>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-2">
              {FOCUS_TAGS.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-volt/30 bg-volt/5 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-volt"
                >
                  {t}
                </span>
              ))}
            </div>

            <p className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-slate-400">
              <MapPin className="h-4 w-4 text-volt" />
              San Diego, CA · Nashville, TN
            </p>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-5 lg:order-2 h-full" delay={0.1}>
          <div className="relative h-full min-h-[340px] sm:min-h-[420px]" data-testid="team-photo-frame">
            <div
              className="absolute -inset-4 -z-10 rounded-[2rem] bg-volt/10 blur-3xl"
              aria-hidden="true"
            />
            <div className="img-blend absolute inset-0">
              <img
                src={jacobPhoto}
                alt="Jacob Lee — Athlete Agent, Pickleball"
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                data-testid="team-photo"
              />
            </div>
            <div className="absolute bottom-2 left-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-volt">
                The Next Generation of Agent
              </p>
              <p className="mt-1 font-display text-lg uppercase text-white">Jacob Lee</p>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7 lg:order-1" delay={0.2}>
          <div className="rounded-3xl border border-white/10 bg-ink p-8 sm:p-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
              Athlete Agent · Pickleball
            </p>
            <h3 className="mt-2 font-display text-4xl uppercase text-white sm:text-5xl">
              Jacob Lee
            </h3>

            <div className="mt-8 space-y-5 text-base leading-relaxed text-slate-300">
              <p>
                Jacob Lee returns home to SMG after an early summer with our
                former agency, where he cut his teeth on brand activation,
                streaming content development and the machinery of athlete
                representation.
              </p>
              <p>
                Today, his focus lives entirely on the pickleball court. He
                brings a strong command of the game, athlete branding and the
                expanding universe of sponsorships, endorsements, content and
                strategic brand integration around it.
              </p>
              <p>
                As part of SMG Sports Management Group International, Jacob
                identifies, develops and represents pickleball players of every
                age and competitive level — helping each athlete build a
                distinctive personal brand, secure meaningful opportunities and
                establish a future that outlasts the scoreboard.
              </p>
            </div>

            <a
              href="mailto:jacob@smgmediagroup.com"
              data-testid="team-email-btn"
              className="group mt-8 inline-flex items-center gap-3 rounded-full bg-volt px-7 py-3.5 font-display text-sm uppercase tracking-wider text-ink transition-colors hover:bg-volt-light"
            >
              <Mail className="h-4 w-4" />
              jacob@smgmediagroup.com
            </a>
          </div>
        </Reveal>
      </div>
    </SectionShell>
  );
}

